import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Checkbox, FormControlLabel, IconButton, InputAdornment, Link, Stack, TextField, Typography } from "@mui/material";
import { getLocalAccessToken } from 'helpers/TokenHelper';
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShAlert } from 'shared/SharedStyles/ShFeedback';
import { ShGreenBtn } from 'shared/SharedStyles/ShInputs';
import { ShMuiLink } from 'shared/SharedStyles/ShNavigation';
import { EmailRegEx } from "shared/constants";
import { IAccountLoginPayload } from "store/slices/auth-v2/auth-v2-model";
import { loginAccount, logoutAccount, setAccountRememberMe } from "store/slices/auth-v2/auth-v2-slice";
import { resetTwoStepAuthState } from 'store/slices/auth-v2/two-step-auth-slice';
import {
    AdminLoginRedirect, AdminLogoutRedirect, CandidateLoginRedirect, CandidateSurveyLogoutRedirect,
    EmployerLoginRedirect, EmployerLogoutRedirect
} from './AuthConstants';
import { IAccountLoginFormMeta, ILoginProps } from "./AuthModel";
import { ForgotPassword } from "./ForgotPassword";
import { SocialLogin } from "./SocialLogin";
import { TwoStepAuthDialog } from './TwoStepAuthDialog';

export const Login = ({ isShowSocialLogin, emailAutoFill, usedIn }: ILoginProps) => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const authToken = getLocalAccessToken();
    const { jobCode } = useParams();
    const socialLoginRef = useRef<any>(null);
    const {
        isRememberMe, isAccountLoggedIn, accountAccess, accountLoginStatus, socialSignUpSource,
        showTwoStepAuthWindow, accountLoginResponseMsg, logoutType } = useAppSelector(state => state.auth.auth);
    const { postingSource } = useAppSelector((state) => state.candidate.applyJobHome);
    const [accountLoginPayload, setAccountLoginPayload] = useState<IAccountLoginPayload>({
        email: emailAutoFill ?? '',
        password: ''
    });
    const [loginFormMeta, setLoginFormMeta] = useState<IAccountLoginFormMeta>({
        emailTouched: false,
        passwordTouched: false,
        emailError: null,
        passwordError: null
    });
    const [isShowForgotPassword, setIsShowForgotPassword] = useState<boolean>(false);
    const [isAuthDialogOpen, setIsAuthDialogOpen] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    // Call logoutAccount if authToken is removed from local storage.
    useEffect(() => {
        if (!authToken) {
            dispatch(logoutAccount({ logoutType: 'auto' }));
        }
    }, [authToken, dispatch])

    const updateRememberMe = () => {
        dispatch(setAccountRememberMe({
            isRememberMe: !isRememberMe,
            email: isRememberMe ? accountLoginPayload.email : ''
        }));
    }

    const updateLoginFormValues = (value: string, field: 'email' | 'password') => {
        setAccountLoginPayload({ ...accountLoginPayload, [field]: value });

        // Update form errors only if form is touched.
        if (field === 'email' && loginFormMeta.emailTouched) {
            setLoginFormMeta({
                ...loginFormMeta, emailError: value?.length === 0 ? 'Please enter email' :
                    new RegExp(EmailRegEx).test(value) ? null : 'Please enter a valid email'
            });
        } else if (field === 'password' && loginFormMeta.passwordTouched) {
            setLoginFormMeta({
                ...loginFormMeta, passwordError: value.length === 0 ? 'Please enter password' : null
            });
        }
    }

    const login = (evt: FormEvent<HTMLFormElement>) => {
        // preventDefault to stop browser reload on submit
        evt.preventDefault();

        const { email, password } = accountLoginPayload;

        // set form errors.
        const formMeta = {
            emailError: email?.length === 0 ? 'Please enter email' :
                new RegExp(EmailRegEx).test(email) ? null : 'Please enter a valid email',
            passwordError: password.length === 0 ? 'Please enter password' : null,
            emailTouched: true,
            passwordTouched: true
        }
        setLoginFormMeta(formMeta);

        // proceed to login only if there are no form errors.
        if (formMeta.emailError === null && formMeta.passwordError === null) {
            dispatch(loginAccount({ payload: accountLoginPayload, loginPage: usedIn }));
        }
    }

    const onAuthDialogClose = (isAuthenticated: boolean) => {
        /* 
            If isAuthenticated is true then the user/device is authenticated.
            Set user login state to true, so that 'Authenticate' wrapper function gives access to the application URLs.
        */
        setIsAuthDialogOpen(false);
        if (isAuthenticated) {
            redirectAfterLogin();
        } else {
            dispatch(logoutAccount({ logoutType: 'auto' }));
        }
        // Reset device auth store on dialog close to ensure it works properly when its toggled again.
        dispatch(resetTwoStepAuthState());
    };

    // Open two step dialog
    useEffect(() => {
        if (showTwoStepAuthWindow) {
            setIsAuthDialogOpen(true);
        }
    }, [showTwoStepAuthWindow]);

    const redirectAfterLogin = useCallback(() => {
        switch (accountAccess?.role) {
            case 'employer':
            case 'hiring_manager':
            case 'owner':
            case 'observer':
            case 'recruiter':
                navigate(EmployerLoginRedirect);
                break;
            case 'admin':
                navigate(AdminLoginRedirect);
                break;
            case 'candidate':
                if (usedIn === 'employer') {
                    navigate(CandidateLoginRedirect);
                }
                break;
            default:
                break;
        }
    }, [accountAccess?.role, navigate, usedIn]);

    const redirectAfterLogout = useCallback(() => {
        switch (usedIn) {
            case 'employer':
                navigate(EmployerLogoutRedirect);
                break;
            case 'admin':
                navigate(AdminLogoutRedirect);
                break;
            case 'candidate_apply_job':
                navigate(`/candidate/apply-job/${jobCode}${postingSource ? ('?ps=' + postingSource) : ''}`);
                break;
            case 'candidate_survey':
                navigate(CandidateSurveyLogoutRedirect);

        }
    }, [jobCode, navigate, postingSource, usedIn])

    // if user is logged in, navigate accordingly to route
    useEffect(() => {
        if (isAccountLoggedIn) {
            redirectAfterLogin();
        }
    }, [isAccountLoggedIn, redirectAfterLogin]);

    // if user is logged out, navigate accordingly to route
    useEffect(() => {
        if (!isAccountLoggedIn && logoutType === 'user') {
            redirectAfterLogout();
        }
    }, [isAccountLoggedIn, logoutType, redirectAfterLogout]);

    // get social login redirect if user signed up with social profile.
    useEffect(() => {
        if (accountLoginStatus === 'failed' && socialSignUpSource) {
            socialLoginRef.current?.handleGetRedirect(socialSignUpSource);
        }
    }, [accountLoginStatus, socialSignUpSource]);

    return (<>
        <Box maxWidth={500}>
            {/* Show forgot password or login panels */}
            {isShowForgotPassword ? <>
                <ForgotPassword setIsShowForgotPassword={setIsShowForgotPassword} />
            </> : <>
                <form id="account_login" onSubmit={(e) => login(e)}>
                    <Stack rowGap={2}>
                        {accountLoginStatus === 'failed' && <ShAlert severity='error' icon={<ErrorIcon />} >
                            {accountLoginResponseMsg}</ShAlert>}
                        {/* {accountLoginStatus === 'failed' && socialSignUpSource &&
                            <ShAlert severity='info'>
                                You have used {socialSignUpSource} to sign up, please click on {socialSignUpSource} icon to login.
                            </ShAlert>
                        } */}
                        <TextField size="small" name='email' id="email" label="Email" fullWidth
                            variant="outlined" value={accountLoginPayload.email}
                            onChange={e => updateLoginFormValues(e.target.value, 'email')}
                            error={loginFormMeta.emailTouched && loginFormMeta.emailError !== null}
                            helperText={loginFormMeta.emailError} />
                        <TextField size="small" name='password' id="password" label="Password" fullWidth
                            type={showPassword ? 'text' : 'password'}
                            variant="outlined" value={accountLoginPayload.password}
                            onChange={e => updateLoginFormValues(e.target.value, 'password')}
                            error={loginFormMeta.passwordTouched && loginFormMeta.passwordError !== null}
                            helperText={loginFormMeta.passwordError}
                            InputProps={{
                                endAdornment: <InputAdornment position='end'>
                                    <IconButton
                                        edge='end' onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            }}
                        />

                        <Stack direction='row' alignItems='center' justifyContent='space-between'>
                            <FormControlLabel control={<Checkbox size='small' checked={isRememberMe}
                                onChange={updateRememberMe} />}
                                label={<Typography variant="body2">Remember Me</Typography>} />
                            <ShMuiLink noUnderline color='inherit' underline='hover' onClick={() => setIsShowForgotPassword(true)}>
                                <Typography variant="body2">Forgot Password ?</Typography></ShMuiLink>
                        </Stack>
                        <Stack alignItems='center' rowGap={3} marginTop={1}>
                            <ShGreenBtn type="submit" variant="outlined" disabled={accountLoginStatus === 'pending'}>
                                {accountLoginStatus === 'pending' ? 'Logging in ...' : 'Login'}
                            </ShGreenBtn>
                            {isShowSocialLogin && <>
                                <Typography variant="caption">Or continue with</Typography>
                                <SocialLogin buttonType="icon" isSignUp={false} usedIn={usedIn} forwardedRef={socialLoginRef} />
                            </>}
                            <Link variant='body2' alignSelf='center' mb={1} target='_blank'
                                href='https://smoothhiring.com/privacy-policy/'>Privacy Policy</Link>
                        </Stack>
                    </Stack>
                </form>
                {/* Two step authorization dialog.
                                Closes only when user responds with any answer. 
                                If user clicks away from the dialog then the emitted event will return undefined, 
                                which will block the dialog from closing.
                            */}
                <TwoStepAuthDialog isAuthDialogOpen={isAuthDialogOpen} usedIn={usedIn}
                    onAuthDialogClose={(isAuthenticated: boolean) => onAuthDialogClose(isAuthenticated)} />
            </>}
        </Box>
    </>)
}